import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Checkbox,
  Paper,
  Tooltip
} from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEndorsmentFormData,
  updateEndorsementFormData
} from 'src/features/rfqDataSlice';
import { customComponentMap, customComponentSettings } from './animalListCustomComponents.js';
import moment from 'moment';

function AnimalListItemHorizontalEditable({ sumInsuredData, groupDetails, productId, saveRow = () => { } }) {
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);

  const sectionId = sumInsuredData?.id;
  const groupId = groupDetails?.id;

  const tableListDatas = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredEntry || [];
  const renderValue = (attr, value) => {
    if (!value) return 'N/A';
    if (['dob', 'dateOfCoverage', 'notInPolicyPeriod'].includes(attr)) {
      return moment(value).format('DD-MM-YYYY');
    }
    return Array.isArray(value) ? value.join(', ') : value;
  };
  const columns = sumInsuredData?.sumInsuredHeaderSorted || [];
  const createdRows = tableListDatas.filter((r) => r.actionType === 'create');
  const deletedRows = tableListDatas.filter((r) => r.actionType === 'delete');

  const [cellEditMap, setCellEditMap] = React.useState({});
  const [cellInputMap, setCellInputMap] = React.useState({});
  const [cellStaticMap, setCellStaticMap] = React.useState({});

  const handleChange = (attr, index, value) => {
    const actualValue = value?.target?.value ?? value;
    const updatedEntryList = [...tableListDatas];
    updatedEntryList[index] = {
      ...updatedEntryList[index],
      [attr]: actualValue,
      savedStatus: false,
      actionType: (updatedEntryList[index].actionType == null || updatedEntryList[index].actionType == '') ? 'update' : updatedEntryList[index].actionType
    };

    const updatedGroup = {
      ...endorsementFormData.sumInsuredSectionData[sectionId].groups[groupId],
      sumInsuredEntry: updatedEntryList
    };

    const updatedSection = {
      ...endorsementFormData.sumInsuredSectionData[sectionId],
      groups: {
        ...endorsementFormData.sumInsuredSectionData[sectionId].groups,
        [groupId]: updatedGroup
      }
    };

    dispatch(updateEndorsementFormData(
      JSON.parse(JSON.stringify({
        sumInsuredSectionData: {
          ...endorsementFormData.sumInsuredSectionData,
          [sectionId]: updatedSection
        }
      }))));
  };

  const formatValue = (val: unknown): string => {
    if (!val) return 'N/A';

    // If it's already a Date
    if (val instanceof Date) {
      const day = String(val.getDate()).padStart(2, '0');
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const year = val.getFullYear();
      return `${day}-${month}-${year}`;
    }

    // If it's an ISO string
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
      const date = new Date(val); // ✅ safe, val is string
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    }

    // If it's an array
    if (Array.isArray(val)) {
      return val.join(', ');
    }

    // Fallback
    return String(val);
  };




  const renderEditableCell = (attr, rowIndex, head, cellKey) => {
    const inputValue = cellInputMap[cellKey] || '';

    if (['dob', 'dateOfCoverage', 'dobOfMamber'].includes(attr)) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={['year', 'month', 'day']}
            inputFormat="dd-MM-yyyy"
            value={inputValue ? moment(inputValue, "DD-MM-YYYY").toDate() : null}
            onChange={(val) => {
              const formattedDate = val ? moment(val).format("DD-MM-YYYY") : null;

              setCellInputMap({
                ...cellInputMap,
                [cellKey]: formattedDate
              });

              handleChange(attr, rowIndex, formattedDate);
            }}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                style={{ minWidth: "180px" }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (attr === 'gender') {
      return (
        <FormControl fullWidth size="small" style={{ minWidth: '180px' }} >
          <InputLabel>Gender</InputLabel>
          <Select
            label="Gender"
            value={inputValue || ''}
            onChange={(e) => {
              const newVal = e.target.value;
              setCellInputMap({
                ...cellInputMap,
                [cellKey]: newVal
              });
              handleChange(attr, rowIndex, e);
            }}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
      );
    }

    const CustomComponent = customComponentMap[attr];
    const CustomSetting = customComponentSettings[attr];
    if (CustomComponent) {
      return (
        <CustomComponent
          tableListDatas={tableListDatas}
          index={rowIndex}
          productId={productId}
          saveRow={saveRow}
          label={head.title || head.sumInsuredHeader.title || attr}
          apiUrl={CustomSetting}
          handleChange={handleChange}
        />
      );
    }

    return (
      <TextField
        style={{ minWidth: '180px' }}
        size="small"
        fullWidth
        type={['age', 'sumInsured', 'premium'].includes(attr) ? 'number' : 'text'}
        value={inputValue}
        onChange={(e) => {
          const newVal = e.target.value;
          setCellInputMap({
            ...cellInputMap,
            [cellKey]: newVal
          });
        }}
        onBlur={() => {
          handleChange(attr, rowIndex, inputValue);
        }}
      />
    );
  };

  const shouldShowHeads = tableListDatas?.some(
    (row) => row.actionType === "update" || row.actionType === null
  );


  return (
    <>
      <TableContainer sx={{ mt: shouldShowHeads ? 1 : 0 }} component={Paper}>
        <Table size="small">
          {shouldShowHeads && <TableHead>
            <TableRow>
              <TableCell>SR NO</TableCell>
              {sumInsuredData.sumInsuredHeaderSorted.map((head) => {
                const attr = head.sumInsuredHeader.attribute;
                return (
                  <TableCell key={attr}>
                    {head.title || head.sumInsuredHeader.title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>}
          <TableBody>
            {tableListDatas?.map((row, rowIndex) => {
              const isHidden =
                row.actionType === "delete" || row.actionType === "create";

              if (isHidden) return null;
              return (
                <TableRow key={rowIndex}>
                  <TableCell>{rowIndex + 1}</TableCell>
                  {sumInsuredData.sumInsuredHeaderSorted.map((head) => {
                    const attr = head.sumInsuredHeader.attribute;
                    const value = row[attr];
                    const cellKey = `${rowIndex}_${attr}`;
                    const isCellEditable = cellEditMap[cellKey] || false;


                    const isEditableAllowed =
                      endorsementFormData?.type === 'Both' ||
                      (endorsementFormData?.type === 'Financial' &&
                        head?.endorsementSubTypes?.financial?.includes(endorsementFormData?.subType)) ||
                      (endorsementFormData?.type === 'Non-Financial' &&
                        head?.endorsementSubTypes?.nonFinancial?.includes(endorsementFormData?.subType));


                    return (
                      <TableCell key={cellKey}>

                        {!isEditableAllowed ? (
                          <Tooltip title="Editing disabled due to subtype restriction">
                            <span>
                              <Checkbox checked={isCellEditable} disabled />
                            </span>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isCellEditable}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCellEditMap({
                                ...cellEditMap,
                                [cellKey]: checked
                              });
                              if (checked) {
                                setCellStaticMap({
                                  ...cellStaticMap,
                                  [cellKey]: value
                                });
                                setCellInputMap({
                                  ...cellInputMap,
                                  [cellKey]: value || ''
                                });
                              }
                            }}
                          />
                        )}

                        <Typography display="inline" sx={{ mr: 1 }}>
                          {cellStaticMap[cellKey] !== undefined
                            ? formatValue(cellStaticMap[cellKey])
                            : formatValue(value)}
                        </Typography>
                        {isCellEditable && (
                          <div style={{ marginTop: 4 }}>
                            {renderEditableCell(attr, rowIndex, head, cellKey)}
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>);
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Created Rows Table */}
      {createdRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Newly Added Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {createdRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#eaffea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Deleted Rows Table */}
      {deletedRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Deleted Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#fdeaea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

    </>
  );
}

export default AnimalListItemHorizontalEditable;
